import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ms } from 'ms';

import { MutationMeta, QueryMeta } from '~/types/query.type';

import { HTTPError, VestigasHTTPError } from '@vestigas/data';
import { DataSyncResponseShapeError } from '~/data/dataSyncJob/responseShapes';

import { errorFactory } from '~/utils/errorFactory';

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: QueryMeta;
    mutationMeta: MutationMeta;
    defaultError: VestigasHTTPError;
  }
}

const MAX_RETRY_AFTER = ms('1m');

/** The response of a rate-limited request, or undefined if it failed for another reason. */
export const rateLimitedResponse = (error: Error): Response | undefined =>
  error instanceof HTTPError && error.response.status === 429
    ? error.response
    : undefined;

/** TanStack Query's own default: exponential backoff capped at 30 seconds. */
const backoffDelay = (attemptIndex: number) =>
  Math.min(ms('1s') * 2 ** attemptIndex, ms('30s'));

/**
 * How long to wait before retrying.
 *
 * A rate limit's window is server-side and what is left of it comes back in
 * `Retry-After`, so waiting exactly that long is what makes the retry succeed. The
 * header is either a number of seconds or an HTTP date (RFC 9110). Everything else -
 * another kind of error, or a rate limit whose header is missing because a
 * cross-origin deployment does not expose it - falls back to the standard backoff.
 */
const retryDelay = (attemptIndex: number, error: Error) => {
  const retryAfter = rateLimitedResponse(error)?.headers.get('Retry-After');

  if (!retryAfter) {
    return backoffDelay(attemptIndex);
  }

  const seconds = Number(retryAfter);
  const milliseconds = Number.isNaN(seconds)
    ? Date.parse(retryAfter) - Date.now()
    : seconds * 1000;

  // An unparsable header, or a date that has already passed, says nothing about how
  // long to wait - the standard backoff is the safer answer than retrying instantly.
  return Number.isNaN(milliseconds) || milliseconds <= 0
    ? backoffDelay(attemptIndex)
    : Math.min(milliseconds, MAX_RETRY_AFTER);
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A rate limit is transient and recovers on its own, so it must not replace the
      // screen with the error boundary - the failing view keeps its own error state and
      // the error factory explains the wait in a toast.
      throwOnError: (error) => !rateLimitedResponse(error),
      retryDelay,
      retry(failureCount, error) {
        if (error instanceof DataSyncResponseShapeError) {
          // The response cannot be worked with; asking for it again does not
          // change that.
          return false;
        }

        if (rateLimitedResponse(error)) {
          // A single retry, once the window the server named has passed.
          return failureCount < 1;
        }

        if (
          error instanceof HTTPError &&
          [401, 403, 404, 410, 422].includes(error.response.status)
        ) {
          // Don't retry if the request is unauthorized (401), forbidden (403), invalid (422) or the resource is not found (404) / gone (410).
          return false;
        }

        if (
          error instanceof HTTPError &&
          [500].includes(error.response.status)
        ) {
          // Retry once if the request fails with a 500 error.
          return failureCount <= 1;
        }

        // Retry up to 10 times if needed
        return failureCount <= 10;
      },
    },
  },
  queryCache: new QueryCache({
    onError(error, query) {
      errorFactory.handleQueryError(error, query);
    },
  }),
  mutationCache: new MutationCache({
    onError(error, _variables, _onMutateResult, mutation) {
      errorFactory.handleMutationError(error, mutation);
    },
  }),
});
